import React, { useEffect, useRef, useState } from 'react'
import CONFIG from '../../config/controls_config.json';
import ColorPicker from '../ColorPicker/colorpicker';
import FontPicker from '../FontPicker/fontpicker';
import SliderControl from '../SliderControl/slidercontrol';
import DeletePrompt from '../DeletePrompt/deleteprompt';
// imgs
import Plus from '../../img/btn_plus.svg';
import Minus from '../../img/btn_minus.svg';

import './textcontrols.css';

/**
 * TextControls component that displays a list of controls for editing canvas text properties.
 * This component is used to manage text objects in the application, allowing users to update properties like position, size, color, and font.
 * @param {Object} props - The properties passed to the TextControls component.
 * @param {Object} props.content - The content object containing text objects to be displayed and edited.
 * @param {function} props.onSetID - Callback function to set the active text object ID.
 * @param {function} props.onContentUpdate - Callback function to update the properties of a text object.
 * @param {function} props.onContentDelete - Callback function to delete a text object.
 * @returns {JSX.Element} The rendered TextControls component.
 */
const TextControls = ({ content, onSetID, onContentUpdate, onContentDelete }) => {    
    const [txtConfig, setTxtConfig] = useState({});
    const [activeControl, setActiveControl] = useState(null);
    const controls = useRef({});

    useEffect(()=>{        
        const txt_content = content['text_objs'];
        if (!content || !txt_content) return;
        const old_count = Object.keys(txtConfig).length;
        const new_count = Object.keys(txt_content).length;
        setTxtConfig(txt_content);

        // If new text object was added, activate it
        // This gets messed up if we don't skip a frame
        if ( new_count > old_count ){
            const last = Object.keys(txt_content)[new_count-1];
            setTimeout(()=> ActivateControl(last), 1);
        }        
    }, [content]);

    function ControlChange( e, target=null ){
        const inpt = target ? target : e.target;
        if (!inpt.value) return;
        onContentUpdate({
            id: inpt.dataset.id,
            name: inpt.name,
            value: inpt.value
        })        
    }

    function handleControlToggle( id ){
        const control = controls.current[Number(id)];
        const parent = control.parentNode;        
        const decideOpen = !(parent && parent.classList.contains('active'));
        ActivateControl(Number(id), decideOpen);
    }

    function ActivateControl( id, activating=true ){ 
        // This got messed up from txtConfig map so its casted
        // TODO: Fix this
        const ID = !isNaN(id) ? Number(id) : null;
        onSetID( activating ? ID : null);
        setActiveControl( activating ? ID : null );
        // Setup --height css value for control container animations
        if (activating && ID){
            const control = controls.current[ID];
            const parent = control.parentNode;
            if (!parent || !control) {
                console.error(`No control or parent.\nID:[${id}\ncontrol: ${control}\nparent: ${parent}]}]`)
                return;
            }
            const parent_height_set = parent.style?.cssText?.includes('--height');
            if ( !parent_height_set ){
                const height = control.clientHeight;
                control.parentNode.style = `--height: ${height}px;`;
            }
        }
    }

    return ( <>
        { txtConfig && Object.entries(txtConfig).map(( [id, obj], i )=>(
            <div key={id} className='control-main-container'>
                <div className='control-header'>
                    <div className='object-text'>{obj.text}</div>                    
                    <img 
                        alt='Toggle Control'
                        className='control-toggle cursor-pointer' 
                        src={id==activeControl ? Minus : Plus} 
                        onClick={()=>handleControlToggle(id)}
                    ></img>
                </div>
                <div className={`textcontrol-container-parent${ id==activeControl ? ' active' : ''}`}>
                    <div
                        key={id}
                        className={`textcontrol-container flex-list`}
                        ref={ element => controls.current[id]=element }
                    >
                    {   // Config is /src/config/controls_config.json
                        // values have to match the keys (k)
                        Object.entries(obj).map(([k, v], j)=>{
                            if (k==='id') return null;
                            const config = {...CONFIG[k]};
                            if (k==='x'){
                                config.min = -1;
                                config.max = content.width;
                            }else if (k==='y'){
                                config.min = -1;
                                config.max = content.height;
                            }
                            switch( config.type ){
                                case 'range':                                        
                                    return ( <SliderControl
                                        id={obj.id} key={j} name={k} value={v} title={config.title}
                                        min={ config.min }
                                        max={ config.max }
                                        /* onChange={ControlChange} */
                                        onSliderUpdate={ControlChange}
                                    />);
                                case 'text':
                                    return ( <InputControl
                                        id={obj.id} key={j} name={k} value={v} title={config.title}                                            
                                        onChange={ControlChange}
                                    />);
                                case 'select':
                                    return ( <OptionControl 
                                        id={obj.id} key={j} name={k} value={v} title={config.title}
                                        onChange={ControlChange}
                                        options={config.options}
                                    />);
                                case 'color':
                                    return ( <ColorPicker 
                                        id={obj.id} color={obj[k]} title={config.title} key={j} name={k}
                                        onChange={ControlChange}
                                        />);
                                case 'font':
                                    return (<FontPicker 
                                        id={obj.id} key={j} value={obj.font} name={k}
                                        onChange={ControlChange}
                                    />);
                                default:
                                    return null
                            }
                        })
                    }
                    <DeletePrompt onDelete={()=> onContentDelete(id) } /> 
                    </div>                    
                </div>
            </div> 
        )) }
    </>);
}

///////////////////////////////////////////////////////////////////////////////////////////////
// These are too simple to merit a separate file 
const InputControl = ({ id, name, title, value, onChange }) => {
  return (
    <label>{title}
        <textarea
            data-id={id}
            name={name}
            onChange={onChange}
            type={'text'} 
            value={value || ''}
            spellCheck={false}
        ></textarea>
    </label>
  )
}

const OptionControl = ({ id, name, title, value, options, onChange }) => {
  return (    
    <label htmlFor={name}>{title}
    <select
        id={name}
        data-id={id}
        name={name}
        onChange={onChange}
        defaultValue={value}
    >
        {options.map( (o, i) => (
            <option key={i} value={o}>{o}</option>
        ) )}
    </select>
    </label>    
  )
}

export default TextControls;